import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, MapPin, CreditCard, Package, Lock, ChevronRight } from 'lucide-react';
import Header from './components/Header23';
import Footer from './components/Footer23';

const Template23Checkout = () => {
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(1);
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        pincode: '',
        paymentMethod: 'card',
        cardNumber: '',
        cardExpiry: '',
        cardCVV: '',
        cardName: '',
        specialInstructions: '',
        giftWrap: false,
        weddingDate: '',
    });

    const steps = [
        { number: 1, label: 'Shipping', icon: <MapPin size={18} /> },
        { number: 2, label: 'Payment', icon: <CreditCard size={18} /> },
        { number: 3, label: 'Review', icon: <Package size={18} /> },
    ];

    const orderItems = [
        { name: 'Bridal Silk Lehenga', size: 'M', price: 45999, quantity: 1 },
        { name: 'Sangeet Anarkali Set', size: 'S', price: 18500, quantity: 1 },
    ];

    const subtotal = orderItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
    const shipping = 0;
    const total = subtotal + shipping;

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const nextStep = () => {
        if (currentStep < 3) setCurrentStep(currentStep + 1);
    };

    const prevStep = () => {
        if (currentStep > 1) setCurrentStep(currentStep - 1);
    };

    const inputStyle = {
        width: '100%',
        padding: '14px 16px',
        border: '1px solid #E8E0D4',
        borderRadius: '8px',
        fontFamily: "'Nunito Sans', -apple-system, sans-serif",
        fontSize: '14px',
        color: '#1B3A2D',
        backgroundColor: '#FFFDF8',
        outline: 'none',
        transition: 'border-color 0.3s ease',
        boxSizing: 'border-box',
    };

    const labelStyle = {
        fontFamily: "'Nunito Sans', -apple-system, sans-serif",
        fontSize: '13px',
        fontWeight: 600,
        color: '#1B3A2D',
        display: 'block',
        marginBottom: '6px',
    };

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Libre+Baskerville:ital,wght@0,400;0,700;1,400&family=Nunito+Sans:wght@300;400;500;600;700&display=swap');

                .utsav-checkout-layout {
                    display: flex;
                    flex-direction: column;
                    gap: 32px;
                    max-width: 1200px;
                    margin: 0 auto;
                    padding: 120px 24px 80px;
                }
                @media (min-width: 768px) {
                    .utsav-checkout-layout {
                        flex-direction: row;
                        gap: 48px;
                        padding: 140px 24px 80px;
                    }
                }
                .utsav-checkout-form {
                    flex: 1;
                }
                .utsav-checkout-summary {
                    width: 100%;
                }
                @media (min-width: 768px) {
                    .utsav-checkout-summary {
                        width: 380px;
                        flex-shrink: 0;
                    }
                }
                .utsav-form-row {
                    display: grid;
                    grid-template-columns: 1fr;
                    gap: 16px;
                    margin-bottom: 16px;
                }
                @media (min-width: 768px) {
                    .utsav-form-row {
                        grid-template-columns: 1fr 1fr;
                    }
                }
                .utsav-checkout-input:focus {
                    border-color: #C5974B !important;
                }
            `}</style>

            <div style={{ minHeight: '100vh', backgroundColor: '#FFFDF8' }}>
                <Header cartCount={2} />

                {/* Steps Indicator */}
                <div style={{
                    maxWidth: '600px',
                    margin: '0 auto',
                    padding: '100px 24px 0',
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0',
                    }}>
                        {steps.map((step, idx) => (
                            <React.Fragment key={step.number}>
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: '8px',
                                }}>
                                    <div style={{
                                        width: '44px',
                                        height: '44px',
                                        borderRadius: '50%',
                                        backgroundColor: currentStep >= step.number ? '#C5974B' : '#F8F3EC',
                                        color: currentStep >= step.number ? '#FFFFFF' : '#5A5A5A',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        transition: 'all 0.4s ease',
                                        boxShadow: currentStep === step.number ? '0 4px 16px rgba(197, 151, 75, 0.4)' : 'none',
                                    }}>
                                        {currentStep > step.number
                                            ? <Check size={18} />
                                            : step.icon
                                        }
                                    </div>
                                    <span style={{
                                        fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                        fontSize: '12px',
                                        fontWeight: currentStep === step.number ? 700 : 500,
                                        color: currentStep >= step.number ? '#1B3A2D' : '#5A5A5A',
                                        letterSpacing: '0.5px',
                                    }}>
                                        {step.label}
                                    </span>
                                </div>
                                {idx < steps.length - 1 && (
                                    <div style={{
                                        flex: 1,
                                        height: '2px',
                                        backgroundColor: currentStep > step.number ? '#C5974B' : '#E8E0D4',
                                        margin: '0 16px',
                                        marginBottom: '28px',
                                        transition: 'background-color 0.4s ease',
                                    }} />
                                )}
                            </React.Fragment>
                        ))}
                    </div>
                </div>

                <div className="utsav-checkout-layout">
                    {/* Form Section */}
                    <div className="utsav-checkout-form">
                        {/* Step 1: Shipping */}
                        {currentStep === 1 && (
                            <div>
                                <h2 style={{
                                    fontFamily: "'Libre Baskerville', Georgia, serif",
                                    fontSize: '24px',
                                    fontWeight: 700,
                                    color: '#1B3A2D',
                                    margin: '0 0 8px 0',
                                }}>
                                    Shipping Address
                                </h2>
                                <p style={{
                                    fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                    fontSize: '14px',
                                    color: '#5A5A5A',
                                    margin: '0 0 28px 0',
                                }}>
                                    Where should we deliver your festive wear?
                                </p>

                                <div className="utsav-form-row">
                                    <div>
                                        <label style={labelStyle}>First Name</label>
                                        <input
                                            className="utsav-checkout-input"
                                            type="text"
                                            name="firstName"
                                            value={formData.firstName}
                                            onChange={handleChange}
                                            style={inputStyle}
                                            placeholder="Enter first name"
                                        />
                                    </div>
                                    <div>
                                        <label style={labelStyle}>Last Name</label>
                                        <input
                                            className="utsav-checkout-input"
                                            type="text"
                                            name="lastName"
                                            value={formData.lastName}
                                            onChange={handleChange}
                                            style={inputStyle}
                                            placeholder="Enter last name"
                                        />
                                    </div>
                                </div>

                                <div className="utsav-form-row">
                                    <div>
                                        <label style={labelStyle}>Email</label>
                                        <input
                                            className="utsav-checkout-input"
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            style={inputStyle}
                                            placeholder="your@email.com"
                                        />
                                    </div>
                                    <div>
                                        <label style={labelStyle}>Phone</label>
                                        <input
                                            className="utsav-checkout-input"
                                            type="tel"
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleChange}
                                            style={inputStyle}
                                            placeholder="+91 98765 43210"
                                        />
                                    </div>
                                </div>

                                <div style={{ marginBottom: '16px' }}>
                                    <label style={labelStyle}>Address</label>
                                    <input
                                        className="utsav-checkout-input"
                                        type="text"
                                        name="address"
                                        value={formData.address}
                                        onChange={handleChange}
                                        style={inputStyle}
                                        placeholder="House no., Street, Locality"
                                    />
                                </div>

                                <div className="utsav-form-row">
                                    <div>
                                        <label style={labelStyle}>City</label>
                                        <input
                                            className="utsav-checkout-input"
                                            type="text"
                                            name="city"
                                            value={formData.city}
                                            onChange={handleChange}
                                            style={inputStyle}
                                            placeholder="City"
                                        />
                                    </div>
                                    <div>
                                        <label style={labelStyle}>State</label>
                                        <input
                                            className="utsav-checkout-input"
                                            type="text"
                                            name="state"
                                            value={formData.state}
                                            onChange={handleChange}
                                            style={inputStyle}
                                            placeholder="State"
                                        />
                                    </div>
                                </div>

                                <div className="utsav-form-row">
                                    <div>
                                        <label style={labelStyle}>PIN Code</label>
                                        <input
                                            className="utsav-checkout-input"
                                            type="text"
                                            name="pincode"
                                            value={formData.pincode}
                                            onChange={handleChange}
                                            style={inputStyle}
                                            placeholder="6-digit PIN code"
                                        />
                                    </div>
                                    <div>
                                        <label style={labelStyle}>Wedding / Event Date (Optional)</label>
                                        <input
                                            className="utsav-checkout-input"
                                            type="date"
                                            name="weddingDate"
                                            value={formData.weddingDate}
                                            onChange={handleChange}
                                            style={inputStyle}
                                        />
                                    </div>
                                </div>

                                {/* Gift Wrap */}
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    marginTop: '8px',
                                    padding: '16px',
                                    backgroundColor: '#F8F3EC',
                                    borderRadius: '8px',
                                }}>
                                    <input
                                        type="checkbox"
                                        name="giftWrap"
                                        checked={formData.giftWrap}
                                        onChange={handleChange}
                                        style={{ accentColor: '#C5974B', width: '18px', height: '18px' }}
                                    />
                                    <span style={{
                                        fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                        fontSize: '14px',
                                        color: '#1B3A2D',
                                    }}>
                                        Add festive gift wrapping ({'\u20B9'}199)
                                    </span>
                                </div>

                                <div style={{ marginTop: '32px' }}>
                                    <button
                                        onClick={nextStep}
                                        style={{
                                            width: '100%',
                                            padding: '16px',
                                            backgroundColor: '#1B3A2D',
                                            color: '#FFFFFF',
                                            border: 'none',
                                            borderRadius: '10px',
                                            fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                            fontSize: '14px',
                                            fontWeight: 700,
                                            letterSpacing: '1px',
                                            textTransform: 'uppercase',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '8px',
                                        }}
                                    >
                                        Continue to Payment
                                        <ChevronRight size={16} />
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Step 2: Payment */}
                        {currentStep === 2 && (
                            <div>
                                <h2 style={{
                                    fontFamily: "'Libre Baskerville', Georgia, serif",
                                    fontSize: '24px',
                                    fontWeight: 700,
                                    color: '#1B3A2D',
                                    margin: '0 0 8px 0',
                                }}>
                                    Payment Method
                                </h2>
                                <p style={{
                                    fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                    fontSize: '14px',
                                    color: '#5A5A5A',
                                    margin: '0 0 28px 0',
                                }}>
                                    All transactions are secure and encrypted
                                </p>

                                {/* Payment Options */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '28px' }}>
                                    {[
                                        { value: 'card', label: 'Credit / Debit Card' },
                                        { value: 'upi', label: 'UPI Payment' },
                                        { value: 'cod', label: 'Cash on Delivery' },
                                    ].map(method => (
                                        <label
                                            key={method.value}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '12px',
                                                padding: '16px',
                                                border: formData.paymentMethod === method.value
                                                    ? '2px solid #C5974B'
                                                    : '1px solid #E8E0D4',
                                                borderRadius: '10px',
                                                cursor: 'pointer',
                                                backgroundColor: formData.paymentMethod === method.value ? '#FFF9F0' : '#FFFFFF',
                                                transition: 'all 0.3s ease',
                                            }}
                                        >
                                            <input
                                                type="radio"
                                                name="paymentMethod"
                                                value={method.value}
                                                checked={formData.paymentMethod === method.value}
                                                onChange={handleChange}
                                                style={{ accentColor: '#C5974B', width: '18px', height: '18px' }}
                                            />
                                            <span style={{
                                                fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                                fontSize: '14px',
                                                fontWeight: 600,
                                                color: '#1B3A2D',
                                            }}>
                                                {method.label}
                                            </span>
                                        </label>
                                    ))}
                                </div>

                                {/* Card Details */}
                                {formData.paymentMethod === 'card' && (
                                    <div style={{
                                        backgroundColor: '#FFFFFF',
                                        border: '1px solid #E8E0D4',
                                        borderRadius: '12px',
                                        padding: '24px',
                                    }}>
                                        <div style={{ marginBottom: '16px' }}>
                                            <label style={labelStyle}>Cardholder Name</label>
                                            <input
                                                className="utsav-checkout-input"
                                                type="text"
                                                name="cardName"
                                                value={formData.cardName}
                                                onChange={handleChange}
                                                style={inputStyle}
                                                placeholder="Name on card"
                                            />
                                        </div>
                                        <div style={{ marginBottom: '16px' }}>
                                            <label style={labelStyle}>Card Number</label>
                                            <input
                                                className="utsav-checkout-input"
                                                type="text"
                                                name="cardNumber"
                                                value={formData.cardNumber}
                                                onChange={handleChange}
                                                style={inputStyle}
                                                placeholder="1234 5678 9012 3456"
                                            />
                                        </div>
                                        <div className="utsav-form-row">
                                            <div>
                                                <label style={labelStyle}>Expiry</label>
                                                <input
                                                    className="utsav-checkout-input"
                                                    type="text"
                                                    name="cardExpiry"
                                                    value={formData.cardExpiry}
                                                    onChange={handleChange}
                                                    style={inputStyle}
                                                    placeholder="MM/YY"
                                                />
                                            </div>
                                            <div>
                                                <label style={labelStyle}>CVV</label>
                                                <input
                                                    className="utsav-checkout-input"
                                                    type="text"
                                                    name="cardCVV"
                                                    value={formData.cardCVV}
                                                    onChange={handleChange}
                                                    style={inputStyle}
                                                    placeholder="123"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div style={{ display: 'flex', gap: '12px', marginTop: '32px' }}>
                                    <button
                                        onClick={prevStep}
                                        style={{
                                            padding: '16px 24px',
                                            backgroundColor: 'transparent',
                                            color: '#1B3A2D',
                                            border: '1px solid #E8E0D4',
                                            borderRadius: '10px',
                                            fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                            fontSize: '14px',
                                            fontWeight: 600,
                                            cursor: 'pointer',
                                        }}
                                    >
                                        Back
                                    </button>
                                    <button
                                        onClick={nextStep}
                                        style={{
                                            flex: 1,
                                            padding: '16px',
                                            backgroundColor: '#1B3A2D',
                                            color: '#FFFFFF',
                                            border: 'none',
                                            borderRadius: '10px',
                                            fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                            fontSize: '14px',
                                            fontWeight: 700,
                                            letterSpacing: '1px',
                                            textTransform: 'uppercase',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '8px',
                                        }}
                                    >
                                        Review Order
                                        <ChevronRight size={16} />
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Step 3: Review */}
                        {currentStep === 3 && (
                            <div>
                                <h2 style={{
                                    fontFamily: "'Libre Baskerville', Georgia, serif",
                                    fontSize: '24px',
                                    fontWeight: 700,
                                    color: '#1B3A2D',
                                    margin: '0 0 8px 0',
                                }}>
                                    Review Your Order
                                </h2>
                                <p style={{
                                    fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                    fontSize: '14px',
                                    color: '#5A5A5A',
                                    margin: '0 0 28px 0',
                                }}>
                                    Please verify all details before placing your order
                                </p>

                                {/* Shipping Details */}
                                <div style={{
                                    backgroundColor: '#FFFFFF',
                                    border: '1px solid #E8E0D4',
                                    borderRadius: '12px',
                                    padding: '20px',
                                    marginBottom: '20px',
                                }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                        <h3 style={{
                                            fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                            fontSize: '14px',
                                            fontWeight: 700,
                                            color: '#1B3A2D',
                                            margin: 0,
                                        }}>Shipping Address</h3>
                                        <button
                                            onClick={() => setCurrentStep(1)}
                                            style={{
                                                backgroundColor: 'transparent',
                                                border: 'none',
                                                fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                                fontSize: '13px',
                                                color: '#C5974B',
                                                cursor: 'pointer',
                                                textDecoration: 'underline',
                                            }}
                                        >Edit</button>
                                    </div>
                                    <p style={{
                                        fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                        fontSize: '14px',
                                        color: '#5A5A5A',
                                        lineHeight: 1.6,
                                        margin: 0,
                                    }}>
                                        {formData.firstName} {formData.lastName}<br />
                                        {formData.address}<br />
                                        {formData.city}, {formData.state} - {formData.pincode}<br />
                                        {formData.phone}
                                    </p>
                                </div>

                                {/* Payment Details */}
                                <div style={{
                                    backgroundColor: '#FFFFFF',
                                    border: '1px solid #E8E0D4',
                                    borderRadius: '12px',
                                    padding: '20px',
                                    marginBottom: '20px',
                                }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                        <h3 style={{
                                            fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                            fontSize: '14px',
                                            fontWeight: 700,
                                            color: '#1B3A2D',
                                            margin: 0,
                                        }}>Payment Method</h3>
                                        <button
                                            onClick={() => setCurrentStep(2)}
                                            style={{
                                                backgroundColor: 'transparent',
                                                border: 'none',
                                                fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                                fontSize: '13px',
                                                color: '#C5974B',
                                                cursor: 'pointer',
                                                textDecoration: 'underline',
                                            }}
                                        >Edit</button>
                                    </div>
                                    <p style={{
                                        fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                        fontSize: '14px',
                                        color: '#5A5A5A',
                                        margin: 0,
                                    }}>
                                        {formData.paymentMethod === 'card' && `Card ending in ${formData.cardNumber.slice(-4) || '****'}`}
                                        {formData.paymentMethod === 'upi' && 'UPI Payment'}
                                        {formData.paymentMethod === 'cod' && 'Cash on Delivery'}
                                    </p>
                                </div>

                                {/* Special Instructions */}
                                <div style={{ marginBottom: '24px' }}>
                                    <label style={labelStyle}>Special Instructions (Optional)</label>
                                    <textarea
                                        name="specialInstructions"
                                        value={formData.specialInstructions}
                                        onChange={handleChange}
                                        style={{
                                            ...inputStyle,
                                            minHeight: '80px',
                                            resize: 'vertical',
                                        }}
                                        placeholder="E.g., custom blouse measurements, delivery timing preferences..."
                                    />
                                </div>

                                <div style={{ display: 'flex', gap: '12px' }}>
                                    <button
                                        onClick={prevStep}
                                        style={{
                                            padding: '16px 24px',
                                            backgroundColor: 'transparent',
                                            color: '#1B3A2D',
                                            border: '1px solid #E8E0D4',
                                            borderRadius: '10px',
                                            fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                            fontSize: '14px',
                                            fontWeight: 600,
                                            cursor: 'pointer',
                                        }}
                                    >
                                        Back
                                    </button>
                                    <button
                                        style={{
                                            flex: 1,
                                            padding: '16px',
                                            backgroundColor: '#C5974B',
                                            color: '#FFFFFF',
                                            border: 'none',
                                            borderRadius: '10px',
                                            fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                            fontSize: '14px',
                                            fontWeight: 700,
                                            letterSpacing: '1px',
                                            textTransform: 'uppercase',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '10px',
                                        }}
                                    >
                                        <Lock size={16} />
                                        Place Order — {'\u20B9'}{total.toLocaleString('en-IN')}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Order Summary Sidebar */}
                    <div className="utsav-checkout-summary">
                        <div style={{
                            backgroundColor: '#FFFFFF',
                            borderRadius: '16px',
                            padding: '24px',
                            boxShadow: '0 4px 20px rgba(27, 58, 45, 0.08)',
                            position: 'sticky',
                            top: '100px',
                        }}>
                            <h3 style={{
                                fontFamily: "'Libre Baskerville', Georgia, serif",
                                fontSize: '18px',
                                fontWeight: 700,
                                color: '#1B3A2D',
                                margin: '0 0 20px 0',
                            }}>
                                Order Summary
                            </h3>

                            {orderItems.map((item, idx) => (
                                <div key={idx} style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    padding: '12px 0',
                                    borderBottom: idx < orderItems.length - 1 ? '1px solid #F8F3EC' : 'none',
                                }}>
                                    <div>
                                        <p style={{
                                            fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                            fontSize: '14px',
                                            fontWeight: 600,
                                            color: '#1B3A2D',
                                            margin: '0 0 2px 0',
                                        }}>
                                            {item.name}
                                        </p>
                                        <p style={{
                                            fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                            fontSize: '12px',
                                            color: '#5A5A5A',
                                            margin: 0,
                                        }}>
                                            Size: {item.size} | Qty: {item.quantity}
                                        </p>
                                    </div>
                                    <span style={{
                                        fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                        fontSize: '14px',
                                        fontWeight: 700,
                                        color: '#C5974B',
                                    }}>
                                        {'\u20B9'}{item.price.toLocaleString('en-IN')}
                                    </span>
                                </div>
                            ))}

                            <div style={{
                                borderTop: '2px solid #E8E0D4',
                                marginTop: '16px',
                                paddingTop: '16px',
                            }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                    <span style={{
                                        fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                        fontSize: '14px',
                                        color: '#5A5A5A',
                                    }}>Subtotal</span>
                                    <span style={{
                                        fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                        fontSize: '14px',
                                        fontWeight: 600,
                                        color: '#1B3A2D',
                                    }}>{'\u20B9'}{subtotal.toLocaleString('en-IN')}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                                    <span style={{
                                        fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                        fontSize: '14px',
                                        color: '#5A5A5A',
                                    }}>Shipping</span>
                                    <span style={{
                                        fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                        fontSize: '14px',
                                        fontWeight: 600,
                                        color: '#1B3A2D',
                                    }}>FREE</span>
                                </div>
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    paddingTop: '12px',
                                    borderTop: '1px solid #E8E0D4',
                                }}>
                                    <span style={{
                                        fontFamily: "'Libre Baskerville', Georgia, serif",
                                        fontSize: '16px',
                                        fontWeight: 700,
                                        color: '#1B3A2D',
                                    }}>Total</span>
                                    <span style={{
                                        fontFamily: "'Libre Baskerville', Georgia, serif",
                                        fontSize: '20px',
                                        fontWeight: 700,
                                        color: '#C5974B',
                                    }}>{'\u20B9'}{total.toLocaleString('en-IN')}</span>
                                </div>
                            </div>

                            {/* Security Badge */}
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px',
                                marginTop: '20px',
                                padding: '12px',
                                backgroundColor: '#F8F3EC',
                                borderRadius: '8px',
                            }}>
                                <Lock size={14} color="#1B3A2D" />
                                <span style={{
                                    fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                    fontSize: '12px',
                                    color: '#5A5A5A',
                                }}>
                                    256-bit SSL Encrypted Payment
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <Footer />
            </div>
        </>
    );
};

export default Template23Checkout;
